/**
 * SubscriptionBadge — displays current plan status
 * Free: "Upgrade" button opens checkout modal
 * Active Pro: green "Pro" (+ days remaining if cancel_at_period_end)
 * OPS/ADMIN: green "Pro" badge (internal staff)
 * Expired: orange "Upgrade" opens checkout modal
 */

import { Badge } from '../../../components/ui';
import { useSubscriptionStatus } from '../../../api/hooks';
import { useCheckoutStore } from '../../../stores/checkoutStore';
import { useIsPro, useAuthStore } from '../../../stores/authStore';

export function SubscriptionBadge() {
  const openCheckout = useCheckoutStore((s) => s.openCheckout);
  const { data: subscription } = useSubscriptionStatus();
  const isPro = useIsPro();
  const userRole = useAuthStore((s) => s.user?.user_role?.toUpperCase());

  // ADMIN / OPS — intentionally render nothing in the header. The
  // in-menu Admin shield in the user-avatar dropdown is sufficient for
  // internal staff (Header.tsx:265-270); a permanent header pill is
  // noise on every page for the operator running the platform.
  if (userRole === 'ADMIN' || userRole === 'OPS') {
    return null;
  }

  // Active Stripe subscription with cancel pending — show days remaining
  if (subscription?.is_active && subscription.tier === 'PRO') {
    if (subscription.cancel_at_period_end && subscription.days_remaining != null) {
      return (
        <Badge variant="warning" size="sm" className="cursor-default">
          Pro · {subscription.days_remaining}d left
        </Badge>
      );
    }
    return (
      <Badge variant="success" size="sm" className="cursor-default">
        Pro
      </Badge>
    );
  }

  // Tier-based PRO (manual upgrade) — show Pro badge
  if (isPro) {
    return (
      <Badge variant="success" size="sm" className="cursor-default">
        Pro
      </Badge>
    );
  }

  // Free or expired — show upgrade link
  return (
    <button
      onClick={() => openCheckout()}
      className="text-body-sm font-medium text-orange hover:underline transition-colors"
    >
      Upgrade
    </button>
  );
}
